import { Box, Skeleton, Stack } from '@mantine/core';
import {
	useApiGetIntegrationQueryExecuteAccess,
	useApiSetIntegrationQueryExecuteAccess,
} from '@repo/api-codegen';
import { SettingsMultiSelect } from '@repo/common/components';
import { Text, Title } from '@repo/foundations';
import { useCallback, useMemo } from 'react';
import {
	queryClient,
	useSuspenseFeatureAccess,
	type IIntegration,
} from '../../../../../api';
import { useExtendedUserList } from '../../../../../api/hooks/user/useExtendedUserList';

interface QueryPermissionsV2Props {
	integration: IIntegration;
}

const ROLE_OPTIONS = [
	{
		value: 'admin',
		label: 'Admin',
		iconName: 'crown',
	},
	{
		value: 'editor',
		label: 'Editor',
		iconName: 'edit',
	},
	{
		value: 'viewer',
		label: 'Viewer',
		iconName: 'eye',
	},
];

export function QueryPermissionsV2({ integration }: QueryPermissionsV2Props) {
	const {
		data: currentAccess,
		isLoading: isLoadingAccess,
		queryKey,
	} = useApiGetIntegrationQueryExecuteAccess({
		pathParams: { integrationId: integration.id },
	});

	const { userGroups, isLoading: isLoadingUserGroups } = useExtendedUserList();
	const { groupAccess } = useSuspenseFeatureAccess();

	const { mutateAsync: updateAccess } = useApiSetIntegrationQueryExecuteAccess({
		onMutate: async (data) => {
			queryClient.setQueryData(queryKey, () => data.body);
		},
	});

	const scopes = ['ai', 'metrics', 'preview'] as const;

	const scopeTitles = {
		ai: 'Secoda AI',
		metrics: 'Query blocks',
		preview: 'Previews',
	};

	const roleItems = useMemo(
		() =>
			ROLE_OPTIONS.map((role) => ({
				value: `role:${role.value}`,
				label: role.label,
			})),
		[]
	);

	const groupItems = useMemo(
		() =>
			(userGroups || []).map((group) => ({
				value: `group:${group.id}`,
				label: group.name,
			})),
		[userGroups]
	);

	const combinedItems = useMemo(() => {
		const items = [...roleItems];
		if (groupAccess) {
			items.push(...groupItems);
		}
		return items;
	}, [roleItems, groupItems, groupAccess]);

	const handleCombinedValueChange = useCallback(
		async (values: string[], scope: 'ai' | 'metrics' | 'preview') => {
			const roleValues = values
				.filter((v) => v.startsWith('role:'))
				.map((v) => v.replace('role:', ''));
			const groupValues = values
				.filter((v) => v.startsWith('group:'))
				.map((v) => v.replace('group:', ''));

			const currentAccessList = currentAccess?.execute_access || [];
			const othersAccessList = currentAccessList.filter(
				(access) => !(access.scope === scope)
			);

			const newAccessList = [
				...othersAccessList,
				...roleValues.map((value) => ({
					namespace: 'role' as const,
					value,
					scope,
				})),
				...groupValues.map((value) => ({
					namespace: 'group' as const,
					value,
					scope,
				})),
			];

			await updateAccess({
				pathParams: { integrationId: integration.id },
				body: { execute_access: newAccessList },
			});
		},
		[currentAccess, updateAccess, integration.id]
	);

	if (isLoadingAccess || isLoadingUserGroups) {
		return <Skeleton height={300} />;
	}

	return (
		<Stack spacing="md">
			<Stack spacing={0}>
				<Title size="md">Query access</Title>
				<Text size="sm" color="text/secondary/default">
					Query authentication
				</Text>
			</Stack>
			<Box>
				{scopes.map((scope) => {
					const scopeTitle = scopeTitles[scope];
					const roleValues =
						currentAccess?.execute_access
							.filter(
								(access) =>
									access.namespace === 'role' && access.scope === scope
							)
							.map((access) => `role:${access.value}`) || [];

					// This handles cases where a group is deleted after its been added somewhere
					// We filter here so the UI is correct, and if updates happen it'll propagate correctly
					const validGroupValues = new Set(
						userGroups?.map((group) => `${group.id}`)
					);

					const groupValues =
						currentAccess?.execute_access
							.filter(
								(access) =>
									access.namespace === 'group' &&
									access.scope === scope &&
									validGroupValues.has(access.value)
							)
							.map((access) => `group:${access.value}`) || [];

					const combinedValues = [...roleValues, ...groupValues];

					return (
						<SettingsMultiSelect
							searchable
							key={scope}
							title={scopeTitle}
							options={combinedItems}
							values={combinedValues}
							onChange={(values) => {
								handleCombinedValueChange(values, scope);
							}}
							itemName="selected"
						/>
					);
				})}
			</Box>
		</Stack>
	);
}
